/**
 * Czech — and the FALLBACK, not English.
 *
 * The same decision as the till's. A Czech restaurateur reading English labels
 * in their own back office has been told, politely, that the software is for
 * somebody else.
 */

import type { Messages } from "./en";

export const cs: { locale: string; messages: Messages } = {
  locale: "cs",
  messages: {
    // --- Shell -------------------------------------------------------------
    // --- Sign-in -----------------------------------------------------------

    // --- Role names ---------------------------------------------------------
    // The roster showed raw identifiers — "manager", "staff" — which is the same
    // database key on a screen that made the kitchen header read "KITCHEN".
    "role.owner": "Majitel",
    "role.admin": "Správce",
    "role.manager": "Manažer",
    "role.shift_lead": "Vedoucí směny",
    "role.staff": "Obsluha",
    "role.viewer": "Náhled",
    "signIn.title": "Přihlášení",
    "signIn.subtitle": "Zadejte svůj PIN — stejný, jaký používáte na pokladně.",
    "signIn.staff": "Kdo jste",
    "signIn.staffPlaceholder": "např. Eva S.",
    "signIn.staffHint": "Jméno, jak ho ukazuje pokladna.",
    "signIn.pin": "PIN",
    "signIn.submit": "Přihlásit se",
    "signIn.working": "Přihlašuji…",
    "signIn.badCredentials": "Nesprávné jméno nebo PIN.",
    "signIn.notBackOffice": "Tato role nemá přístup do kanceláře.",
    "signIn.unreachable": "Server neodpovídá. Zkontrolujte připojení.",
    "signIn.tooMany": "Příliš mnoho pokusů. Zkuste to znovu za {minutes} min.",
    "signIn.signedInAs": "Přihlášen(a):",
    "signIn.signOut": "Odhlásit se",
    "signIn.expiresAt": "Relace vyprší",
    // --- Command palette ---------------------------------------------------
    "palette.label": "Přejít na obrazovku",
    "palette.placeholder": "Hledat obrazovku…",
    "palette.open": "Hledat",
    "palette.here": "jste tady",
    "palette.nothing": "Nic takového tu není",
    "palette.hintMove": "pohyb",
    "palette.hintOpen": "otevřít",
    "palette.hintClose": "zavřít",
    "shell.menu": "Menu",
    "shell.closeMenu": "Zavřít menu",

    "app.title": "Kancelář",
    "nav.today": "Dnes",
    "nav.sales": "Tržby",
    "nav.checks": "Účtenky",
    "nav.shifts": "Směny",
    "nav.kitchen": "Kuchyně",
    "nav.fiscal": "Daně",
    "nav.tableCards": "Karty na stoly",
    "tableCards.lede": "Vytiskněte jednu kartu na stůl. Host ji naskenuje, otevře si účet a objedná — nikdo nemusí přijít.",
    "tableCards.tablesLabel": "Stoly",
    "tableCards.placeholder": "12\n13\nterasa 3",
    "tableCards.hint": "Jeden stůl na řádek, tak jak jim říkáte. Počet karet:",
    "tableCards.duplicates": "Dvakrát je tu:",
    "tableCards.print": "Vytisknout karty",
    "tableCards.expires": "Karty přestanou platit",
    "tableCards.callToAction": "Naskenujte a objednejte",
    "tableCards.modeWarning": "Tato provozovna otevírá účty na pokladně. Karty se vytisknou, ale dokud model nepřepnete v Nastavení, host uvidí „zeptejte se obsluhy“.",
    "tableCards.noBaseUrl": "Není nastaveno, kde běží aplikace pro hosty, takže karta nese jen kód. Fotoaparát ho nabídne zkopírovat místo otevřít.",
    "nav.menu": "Menu",
    "nav.staff": "Lidé",
    "nav.settings": "Nastavení",
    "nav.group.running": "Provoz",
    "nav.group.money": "Peníze",
    "nav.group.setup": "Nastavení",
    "nav.group.soon": "Připravujeme",

    // --- Common ------------------------------------------------------------
    "common.loading": "Načítám…",
    "common.failed": "Nepodařilo se načíst",
    "common.retry": "Zkusit znovu",
    "common.empty": "Zatím nic",
    "common.today": "Dnes",
    "common.week": "Posledních 7 dní",
    "common.month": "Posledních 30 dní",
    "common.custom": "Vlastní",
    "common.from": "Od",
    "common.to": "Do",
    "common.unreachable": "Server neodpověděl. Tady nejsou nuly — hodnoty nejsou známé.",
    "common.of": "z",
    "common.total": "Celkem",
    "common.date": "Datum",
    "common.time": "Čas",
    "common.none": "Žádné",

    // --- Today -------------------------------------------------------------
    "today.takings": "Dnešní tržba",
    "today.checks": "Účtenky",
    "today.average": "Průměrný účet",
    "today.open": "Otevřené stoly",
    "today.openMoney": "Na otevřených stolech",
    "today.vat": "DPH",
    "today.payments": "Způsoby platby",
    "today.quiet": "Dnes se zatím nic neprodalo.",
    "today.quietHint": "Obchodní den začíná ve 4:00.",

    // --- Sales -------------------------------------------------------------
    "sales.heading": "Tržby",
    "sales.day": "Obchodní den",
    "sales.checkCount": "Účtenek",
    "sales.revenue": "Tržba",
    "sales.breakdown": "Rozpad tržeb",
    "sales.tab.items": "Položky",
    "sales.tab.staff": "Obsluha",
    "sales.tab.hours": "Hodiny",
    "sales.category": "Kategorie",
    "sales.item": "Položka",
    "sales.quantity": "Množství",
    "sales.discounts": "Slevy",
    "sales.server": "Obsluha",
    "sales.voids": "Storna",
    "sales.hour": "Hodina",
    "sales.other": "Ostatní",
    "sales.vat": "DPH",
    "sales.net": "Bez DPH",
    "sales.covers": "Hostů",
    "sales.perCover": "Na hosta",
    "sales.coversHint": "{n} hostů u stolů — prodej u pultu se nepočítá",
    "sales.average": "Průměr",
    "sales.tips": "Spropitné",
    "sales.rounding": "Zaokrouhlení",
    "sales.byRate": "Podle sazby DPH",
    "sales.byTender": "Podle způsobu platby",
    "sales.gaps": "Dny bez tržby",
    "sales.gapsWhy":
      "Chybějící obchodní den brání uzavření období — DPH na výstupu se váže k datu uskutečnění plnění, takže pozdě podaný den znamená dodatečné přiznání.",
    "sales.tipsNotRevenue": "Spropitné je závazek vůči personálu, není to tržba.",

    // --- Checks ------------------------------------------------------------
    "checks.heading": "Účtenky",
    "checks.number": "Číslo",
    "checks.table": "Stůl",
    "checks.opened": "Otevřeno",
    "checks.closed": "Uzavřeno",
    "checks.state": "Stav",
    "checks.stateOpen": "Otevřený",
    "checks.openSince": "Otevřený od {day}",
    "checks.stateSettled": "Uzavřený",
    "checks.outstanding": "Zbývá uhradit",
    "checks.staff": "Obsluhoval",
    "checks.lines": "Položky",
    "checks.voided": "Stornováno",
    "voidReason.RUNG_IN_ERROR": "Špatně naúčtováno",
    "voidReason.GUEST_CHANGED_MIND": "Host si to rozmyslel",
    "voidReason.SENT_BACK": "Vráceno z kuchyně",
    "voidReason.SPILLED": "Rozlito / spadlo",
    "voidReason.OTHER": "Jiný důvod",
    "checks.tenders": "Platby",
    "checks.anomalies": "Co nesedělo",
    "checks.noNumber": "Bez čísla účtenky",
    "checks.tab": "Účet {n}",
    "checks.fiscal.reported": "Nahlášeno finanční správě",
    "checks.fiscal.test": "Potvrzeno TESTOVACÍ službou",
    "checks.fiscal.testWhy": "Testovací potvrzení nic nehlásí. Tato tržba není evidována.",
    "checks.fiscal.refused": "Odmítnuto",
    "checks.fiscal.pending": "Zatím nenahlášeno",
    "checks.fiscal.none": "Pro tuto tržbu nebylo nikdy zařazeno podání",

    // --- Shifts ------------------------------------------------------------
    "shifts.heading": "Směny",
    "shifts.cashier": "Pokladní",
    "shifts.expected": "Očekáváno",
    "shifts.counted": "Napočítáno",
    "shifts.variance": "Rozdíl",
    "shifts.open": "Stále otevřená",
    "shifts.uncounted": "Zatím nepočítáno",
    "shifts.over": "Přebytek",
    "shifts.short": "Manko",
    "shifts.exact": "Přesně",
    "shifts.ok": "V toleranci",
    "shifts.watch": "Stojí za pohled",
    "shifts.alert": "Je třeba prošetřit",
    "shifts.breakdown": "Z čeho očekávaná částka vychází",
    "shifts.breakdownShort": "Rozpis",
    "shifts.openingFloatMinor": "Počáteční hotovost",
    "shifts.cashSalesMinor": "Tržba v hotovosti",
    "shifts.cashRefundsMinor": "Vrácené hotovostí",
    "shifts.roundingMinor": "Zaokrouhlení",
    "shifts.payInsMinor": "Vklady",
    "shifts.payOutsMinor": "Výdaje",
    "shifts.safeDropsMinor": "Odvody do trezoru",
    "shifts.cashTipsMinor": "Spropitné v hotovosti",
    "shifts.tipOutsMinor": "Vyplacené spropitné",
    "shifts.movements": "Pohyby hotovosti",
    "shifts.overIsNotSafe":
      "Opakovaný přebytek hlásíme stejně hlasitě jako manko: bývá to hotovost vyjmutá z pokladny a zatím neodnesená.",
    "shifts.unattributed":
      "Hotovost zaúčtovaná bez otevřené pokladny. Není v žádném Z-reportu níže a nemá se s čím srovnat.",
    "shifts.ambiguous": "z toho s dvěma otevřenými pokladnami najednou",

    // --- Kitchen -----------------------------------------------------------
    "kitchen.heading": "Kuchyně",
    "kitchen.median": "Medián doby na výdej",
    "kitchen.p90": "Nejpomalejší desetina",
    "kitchen.tickets": "Tikety",
    "kitchen.served": "Vydáno",
    "kitchen.station": "Stanice",
    "kitchen.late": "Pozdní tikety",
    "kitchen.whyPercentile": "Percentily, ne průměr — host zažívá ten konec, ne střed.",

    // --- Fiscal ------------------------------------------------------------
    "fiscal.heading": "Finanční správa",
    "fiscal.eet": "EET",
    "fiscal.notSetUp": "Nenastaveno",
    "fiscal.notSetUpWhy": "Povinné od 1. 1. 2027. DIČ poplatníka a číslo provozovny se zadávají v Nastavení.",
    "fiscal.credentials": "Podpisový certifikát",
    "fiscal.present": "Nahraný",
    "fiscal.absent": "Chybí",
    "fiscal.absentWhy": "Bez podpisového certifikátu se žádná tržba neodešle.",
    "fiscal.oldestPending": "Nejstarší dosud nenahlášená tržba",
    "fiscal.quarantined": "Trvale odmítnuto",
    "fiscal.attempts": "pokusů",
    "fiscal.andMore": "a další, které tu nejsou:",
    "fiscal.showRows": "Zobrazit další účtenky",
    "fiscal.kind.NO_TILL_ID": "Pokladna nemá fiskální ID",
    "fiscal.kind.NO_RECEIPT_NUMBER": "Účet nemá číslo účtenky",
    "fiscal.kind.NUMBER_OUTSIDE_LEASE": "Číslo účtenky je mimo přidělený rozsah",
    "fiscal.kind.NUMBER_ALREADY_ASSIGNED": "Číslo účtenky už patří jinému účtu",
    "fiscal.playground": "Potvrzeno testovací službou",
    "fiscal.playgroundWhy":
      "Testovací potvrzení nic nevyřizuje. Tyto tržby vypadají jako nahlášené a nahlášené nejsou.",
    "fiscal.unreportable": "Nelze nahlásit",
    "fiscal.clean": "Nic nečeká a nic nebylo odmítnuto.",
    "fiscal.numbering": "Číslování účtenek",
    "fiscal.gaps": "Mezery",
    "fiscal.gapsClean": "Žádné mezery — každé přidělené číslo bylo použito nebo vráceno.",
    "fiscal.issued": "Vydáno",

    // --- Menu --------------------------------------------------------------
    "menu.heading": "Menu",
    "menu.tab.items": "Položky",
    "menu.tab.categories": "Kategorie",
    "menu.tab.groups": "Přílohy a úpravy",
    "menu.tab.stations": "Místa přípravy",
    "menu.search": "Hledat v menu",
    "menu.allCategories": "Vše",
    "menu.uncategorised": "Bez kategorie",
    "menu.category": "Kategorie",
    "menu.newItem": "Nová položka",
    "menu.newCategory": "Nová kategorie",
    "menu.newGroup": "Nová skupina",
    "menu.newStation": "Nové místo přípravy",
    "menu.editItem": "Upravit",
    "menu.off": "Vypnuto",
    "menu.switchOff": "Vypnout",
    "menu.switchOn": "Zapnout",
    "menu.empty": "Zatím tu nic není.",
    "menu.saved": "Uloženo. Na pokladny se dostane po publikování.",
    "menu.saveFailed": "Nepodařilo se uložit: {detail}",
    "menu.priceHint": "Včetně DPH, jak ji uvidí host.",
    "menu.rateAfterPublish": "po publikování",
    "menu.itemCount": "Položek: {count}",
    "menu.usedBy": "U položek: {count}",
    "menu.moveUp": "Výš",
    "menu.moveDown": "Níž",
    "menu.remove": "Odebrat",
    "menu.addOption": "Přidat možnost",
    "menu.required": "Povinné",
    "menu.optional": "Volitelné",
    "menu.noGroups": "Přílohy a úpravy — třeba „Příloha“ nebo „Bez cibule“ — pokladna nabídne při markování. Povinnou otevře sama.",
    "menu.taxCategory": "Daňová kategorie",
    "menu.tax.FOOD": "Jídlo",
    "menu.tax.NON_ALC_BEVERAGE": "Nealkoholický nápoj",
    "menu.tax.ALCOHOL": "Alkohol",
    "menu.tax.TAP_WATER": "Kohoutková voda",
    "menu.tax.MILK_BEVERAGE": "Mléčný nápoj",
    "menu.tax.NON_FOOD": "Nepotravinové zboží",
    "menuImport.open": "Importovat z tabulky",
    "menuImport.title": "Import menu z tabulky",
    "menuImport.lead": "Soubor CSV — z Excelu přes „Uložit jako CSV“, nebo export ze Storyous. Položky se přidají do konceptu; pokladny je neprodávají, dokud menu nezveřejníte.",
    "menuImport.file": "Soubor",
    "menuImport.choose": "Vybrat soubor…",
    "menuImport.noFile": "Žádný soubor nevybrán",
    "menuImport.unreadable": "Soubor se nepodařilo přečíst jako tabulku.",
    "menuImport.header": "První řádek jsou nadpisy",
    "menuImport.column.name": "Název",
    "menuImport.column.price": "Cena s DPH",
    "menuImport.column.category": "Kategorie",
    "menuImport.column.vat": "Sazba DPH",
    "menuImport.column.none": "—",
    "menuImport.columnN": "Sloupec {n}",
    "menuImport.items": "Položek",
    "menuImport.station": "Stanice",
    "menuImport.tax": "Daň",
    "menuImport.uncategorised": "Bez kategorie",
    "menuImport.problems": "Tyto řádky se nepřidají",
    "menuImport.problem.name": "chybí název",
    "menuImport.problem.price": "cena se nedá přečíst",
    "menuImport.taxHint": "Daň se řídí tím, co položka je. Sazbu ze souboru jen porovnáme se zákonem: pivo i Kofola mají u stolu 21 %, alkohol je ale jen pivo.",
    "menuImport.mismatches": "Sazba v souboru nesedí se zákonem — položky se přidají, zkontrolujte je",
    "menuImport.mismatch": "v souboru {file} %, u stolu {rate} %",
    "menuImport.line": "řádek {line}",
    "menuImport.more": "a dalších {count}",
    "menuImport.add": "Přidat do konceptu ({count})",
    "menuImport.adding": "Přidávám…",
    "menuImport.done": "Přidáno do konceptu: {created}.",
    "menuImport.skipped": "Už v menu bylo, přeskočeno: {count}.",
    "menuImport.publishHint": "Zkontrolujte je a zveřejněte menu — do té doby je pokladny neprodávají.",
    "menuImport.close": "Hotovo",
    "setup.heading": "Než začnete prodávat",
    "setup.progress": "Hotovo {done} z {total}",
    "setup.hide": "Skrýt",
    "setup.open": "Otevřít",
    "setup.step.company": "Firma na účtence — název a IČO",
    "setup.step.menu": "Menu, zveřejněné na pokladny",
    "setup.step.tables": "Stoly",
    "setup.step.people": "Lidé a jejich PINy",
    "setup.step.publish": "Podnik zveřejněný na pokladny",
    "setup.step.devices": "Spárované pokladny a kuchyňské obrazovky",
    "setup.step.pohoda": "Pokladny v Pohodě (pro účetní)",
    "menuImport.failed": "Import se nepodařil: {detail}",
    "menu.field.name": "Název",
    "menu.field.price": "Cena pro hosta",
    "menu.field.description": "Popis",
    "menu.field.ageRestricted": "Jen od 18 let",
    "menu.field.active": "V nabídce",
    "menu.field.groups": "Přílohy a úpravy",
    "menu.field.min": "Musí vybrat alespoň",
    "menu.field.max": "Může vybrat nejvýš",
    "menu.field.maxUnlimited": "bez omezení",
    "menu.field.options": "Možnosti",
    "menu.field.optionName": "Možnost",
    "menu.field.priceDelta": "Příplatek",
    "menu.field.maxQuantity": "Nejvýš ks",
    "menu.problem.name": "Skupina potřebuje název.",
    "menu.problem.optionName": "Každá možnost potřebuje název.",
    "menu.problem.price": "Příplatek musí být částka, třeba 25 nebo 25,50.",
    "menu.problem.tooFew": "Vyžaduje víc voleb, než nabízí — položku s ní by nešlo namarkovat.",
    "menu.problem.max": "Maximum musí být celé číslo, alespoň rovné minimu.",
    "allergen.GLUTEN": "Lepek",
    "allergen.CRUSTACEANS": "Korýši",
    "allergen.EGGS": "Vejce",
    "allergen.FISH": "Ryby",
    "allergen.PEANUTS": "Arašídy",
    "allergen.SOYBEANS": "Sója",
    "allergen.MILK": "Mléko",
    "allergen.NUTS": "Ořechy",
    "allergen.CELERY": "Celer",
    "allergen.MUSTARD": "Hořčice",
    "allergen.SESAME": "Sezam",
    "allergen.SULPHITES": "Siřičitany",
    "allergen.LUPIN": "Vlčí bob",
    "allergen.MOLLUSCS": "Měkkýši",
    "menu.edit": "Upravit cenu",
    "menu.editing": "Nová cena v haléřích bez DPH? Ne — zadejte cenu, kterou uvidí host.",
    "menu.save": "Uložit",
    "menu.cancel": "Zpět",
    "menu.available": "V prodeji",
    "menu.soldOut": "Došlo",
  "menu.availability": "Dostupnost",
  "menu.availabilityFailed": "Nepodařilo se načíst, co je právě vyprodané. Sloupec Dostupnost nemusí být aktuální.",
  "menu.markSoldOut": "Označit jako došlé",
  "menu.soldOutReason": "Důvod",
  "menu.soldOutDefault": "Došlo",
  "menu.soldOutConfirm": "Stáhnout z nabídky",
  "menu.backOn": "Vrátit do nabídky",
    "menu.draft": "Změny se projeví až po publikování.",
    "menu.publish": "Publikovat novou verzi",
    "menu.publishing": "Publikuji…",
    "menu.published": "Publikováno jako verze {version}. Pokladny si ji načtou do minuty.",
    "menu.editFailed": "Změnu se nepodařilo uložit.",
    "menu.version": "Verze",
    "menu.item": "Položka",
    "menu.station": "Připravuje",
    "menu.dineIn": "Na místě",
    "menu.takeaway": "S sebou",
    "menu.rate": "DPH",
    "menu.allergens": "Alergeny",
    "menu.whyTwoPrices":
      "Totéž jídlo má na místě 12 % a s sebou 21 %. Sazba se odvozuje u každé položky, neukládá se k produktu.",

    // --- Staff & settings --------------------------------------------------
    "staff.heading": "Lidé",
    "staff.name": "Jméno",
    "staff.role": "Role",
    "staff.add": "Přidat člověka",
    "staff.nameHint": "Tak, jak ho uvidí na pokladně — „Jana N.“",
    "staff.pin": "PIN",
    "staff.newPin": "Nový PIN",
    "staff.pinHint": "Šest až dvanáct číslic. Řekněte mu ho — nikde se už neukáže.",
    "staff.pinResetHint": "Nechte prázdné, pokud PIN neměníte.",
    "staff.active": "Pracuje u nás",
    "staff.added": "Přidáno. Na pokladně se objeví při příští synchronizaci.",
    "staff.saved": "Uloženo.",
    "staff.savedTills": "Uloženo. Pokladny zapomenou starý PIN i roli při příští synchronizaci.",
    "staff.left": "Už u nás nepracují",
    "staff.count": "{n} v týmu",
    "venue.version": "Na pokladnách verze",
    "venue.unpublished": "Změny ještě nejsou na pokladnách",
    "venue.publish": "Zveřejnit na pokladny",
    "venue.saved": "Uloženo. Na pokladny se dostane po zveřejnění.",
    "venue.published": "Zveřejněno — pokladny si ho stáhnou hned.",
    "venue.company": "Firma na účtence",
    "venue.guests": "Hosté",
    "venue.guestOrdering": "Hosté mohou objednávat z telefonu",
    "venue.guestOrderingLead": "QR kód na účtu ukáže útratu vždy. Objednávat z něj jde, jen když je tohle zapnuté — platí hned, bez zveřejnění.",
    "venue.companyLead": "Tak, jak to musí stát na daňovém dokladu.",
    "venue.legalName": "Obchodní firma",
    "venue.registrationId": "IČO",
    "venue.vatId": "DIČ (jen plátce DPH)",
    "venue.address": "Sídlo",
    "venue.eic": "EET: DIČ poplatníka",
    "venue.unit": "EET: číslo provozovny",
    "venue.tables": "Stoly",
    "venue.addTable": "Přidat stůl",
    "venue.noArea": "Bez oblasti",
    "venue.seats": "míst",
    "venue.seatsLabel": "Počet míst",
    "venue.newTable": "Nový stůl",
    "venue.editTable": "Upravit stůl",
    "venue.ownerOnly": "Upravit může jen majitel.",
    "venue.seats.one": "místo",
    "venue.seats.few": "místa",
    "venue.seats.many": "místa",
    "venue.seats.other": "míst",
    "venue.tableName": "Název",
    "venue.area": "Oblast",
    "venue.tableActive": "Používá se",
    "devices.heading": "Zařízení",
    "devices.pair": "Spárovat zařízení",
    "devices.codeFor": "Kód pro",
    "devices.codeHow": "Na novém zařízení otevřete aplikaci a zadejte ho.",
    "devices.codeUntil": "Platí jednou, do",
    "devices.done": "Hotovo",
    "devices.name": "Název",
    "devices.namePlaceholder": "např. Tablet u baru",
    "devices.kind": "Druh",
    "devices.till": "Pokladna",
    "devices.kitchen": "Kuchyňská obrazovka",
    "devices.station": "Stanoviště",
    "devices.makeCode": "Vytvořit kód",
    "devices.unpair": "Odpárovat",
    "devices.place": "Kde stojí",
    "devices.atTables": "U stolů",
    "devices.atCounter": "U pultu — zaplatit hned, číslo objednávky",
    "devices.pickupScreen": "Výdej — obrazovka s čísly pro hosty",
    "devices.counterChip": "Pult {prefix}",
    "devices.toCounter": "K pultu",
    "devices.toTables": "Ke stolům",
    "venue.noTables": "Zatím tu nejsou žádné stoly. Přidejte první — pokladny je uvidí po zveřejnění.",
    "devices.none": "Zatím tu není žádné zařízení. První pokladnu spárujete kódem.",

    // --- The accountant's file (Pohoda) -----------------------------------
    "tender.CASH": "Hotově",
    "tender.CARD": "Kartou",
    "tender.MEAL_VOUCHER": "Stravenky",
    "tender.GIFT_CARD": "Dárková karta",
    "tender.HOUSE_ACCOUNT": "Na účet podniku",
    "tender.QR_TRANSFER": "QR platba",
    "tender.VOUCHER": "Poukaz",
    "tender.ROOM_CHARGE": "Na pokoj",
    "tender.EXTERNAL": "Jiná platba",
    "pohoda.heading": "Pro účetní",
    "pohoda.lead": "Tržby jako soubor pro Pohodu: za každý den a způsob platby jeden příjmový pokladní doklad, rozdělený podle sazeb DPH.",
    "pohoda.month": "Měsíc",
    "pohoda.toYesterday": "do včerejška",
    "pohoda.download": "Stáhnout pro Pohodu",
    "pohoda.downloaded": "Staženo. Soubor naimportujte v Pohodě přes XML import.",
    "pohoda.noCashAccount": "Chybí pokladna v Pohodě pro:",
    "pohoda.fixInSettings": "Doplňte ji v Nastavení.",
    "pohoda.noRegistrationId": "Firma nemá IČO. Doplňte ho v Nastavení.",
    "pohoda.nothing": "V tomto období se nic neprodalo.",
    "pohoda.dayNotClosed": "Dnešní den ještě neskončil.",
    "pohoda.notSetUp": "Nastavení pro Pohodu je neplatné. Zkontrolujte ho v Nastavení.",
    "pohoda.training": "Toto je školení: nic odsud se neúčtuje, takže pro Pohodu není co exportovat.",
    "pohoda.settings": "Pohoda",
    "pohoda.settingsLead": "Kam se v Pohodě účtuje každý způsob platby. Zkratky pokladen a předkontací opište přesně tak, jak je máte v Pohodě — nejlépe od své účetní.",
    "pohoda.cashAccount": "Pokladna",
    "pohoda.accounting": "Předkontace",
    "pohoda.optional": "nepovinné",
    "pohoda.saved": "Uloženo.",
    "pohoda.tooLong": "Nejvýš 19 znaků.",
    "devices.confirmUnpair": "Odpárovat „{name}“? Přestane fungovat při dalším spojení se serverem.",
    "settings.heading": "Nastavení",
    "settings.venue": "Provozovna",
    "settings.timezone": "Časové pásmo",
    "settings.dayStart": "Obchodní den začíná",
    "settings.fiscalId": "Daňová registrace",
    "settings.devices": "Pokladny",
    "settings.language": "Jazyk",

    // --- Preview -----------------------------------------------------------
    "preview.badge": "Připravujeme",
    "preview.note":
      "Tohle je náčrt obrazovky, která zatím není hotová. Nic z toho nejsou skutečná data.",
    "preview.stock": "Sklad",
    "preview.purchasing": "Faktury od dodavatelů",
    "preview.rota": "Rozpisy směn",
    "preview.analytics": "Analytika",
    "preview.loyalty": "Věrnost",
    "modules.title": "Moduly",
    "modules.lead": "Co tento podnik používá. Prodej, účtenky, pokladna a EET jsou vždy zapnuté; ostatní zapnete v rámci svého tarifu.",
    "modules.plan": "Tarif: {plan}",
    "modules.noPlan": "Bez tarifu — jen prodej a účtenky.",
    "modules.planLapsed": "Tarif vypršel, volitelné moduly jsou vypnuté. Prodej běží dál.",
    "modules.preset": "Typ podniku",
    "modules.custom": "Vlastní výběr",
    "modules.save": "Uložit moduly",
    "modules.saved": "Uloženo. Pokladny a obrazovky to uvidí během pár sekund.",
    "modules.notInPlan": "Není v tarifu",
    "modules.needs": "Potřebuje: {modules}",
    "modules.always": "Vždy zapnuto",
    "modules.soon": "Připravujeme",
    "modules.noServiceMode": "Nechte zapnuté stoly, pult, nebo obojí — jinak se nedá prodávat.",
    "preset.cafe": "Kavárna",
    "preset.restaurant": "Restaurace",
    "preset.cafe-restaurant": "Kavárna + restaurace",
    "preset.pub": "Hospoda / bar",
    "preset.bakery": "Pekárna / take-away",
    "preset.food-truck": "Pojízdné občerstvení",
    "module.sales.name": "Prodej a účtenky",
    "module.sales.lead": "Pokladna, platby, účtenky, směny, EET, menu, lidé a tržby.",
    "module.tables.name": "Stoly",
    "module.tables.lead": "Plánek stolů, účty u stolu, přesuny a rozdělení.",
    "module.counter.name": "Pult",
    "module.counter.lead": "Prodej u pultu: zaplatit hned, číslo objednávky, tady nebo s sebou.",
    "module.kitchen-display.name": "Kuchyňská obrazovka",
    "module.kitchen-display.lead": "Lístky na obrazovce v kuchyni a na baru, alergeny, odbavení.",
    "module.kitchen-printing.name": "Tisk do kuchyně",
    "module.kitchen-printing.lead": "Lístky na tiskárnu v kuchyni a na baru.",
    "module.courses.name": "Chody",
    "module.courses.lead": "Předkrm hned, hlavní chod až na zavolání.",
    "module.guest-ordering.name": "Objednávky od stolu",
    "module.guest-ordering.lead": "Host si objedná z telefonu přes QR kód na stole.",
    "module.public-menu.name": "Menu na webu",
    "module.public-menu.lead": "Veřejné menu s fotkami a alergeny, v několika jazycích.",
    "module.online-ordering.name": "Objednávky s sebou online",
    "module.online-ordering.lead": "Objednávka a platba předem, vyzvednutí u pultu v daný čas.",
    "module.reservations.name": "Rezervace",
    "module.reservations.lead": "Rezervace stolů z webu i po telefonu, na plánku stolů.",
    "module.stock.name": "Sklad",
    "module.stock.lead": "Suroviny, receptury, odpis prodejem, příjemky, inventury, food cost.",
    "module.accounting.name": "Účetnictví",
    "module.accounting.lead": "Tržby jako soubor pro Pohodu.",
    "module.loyalty.name": "Věrnostní program",
    "module.loyalty.lead": "Body a odměny pro stálé hosty.",
    "module.crm.name": "Hosté",
    "module.crm.lead": "Kdo k vám chodí a jak často.",
    "module.foodsafety.name": "Bezpečnost potravin",
    "module.foodsafety.lead": "Záznamy HACCP a kontroly.",

    // --- Stock (the Sklad screens) ---------------------------------------------
    "nav.reservations": "Rezervace",
    "res.title": "Rezervace",
    "res.lead": "Kdo dnes přijde, kolik míst je kdy obsazeno, a rezervace po telefonu.",
    "res.prev": "Předchozí den",
    "res.next": "Další den",
    "res.today": "Dnes",
    "res.covers": "Hostů",
    "res.bookings": "Rezervací",
    "res.capacity": "Míst najednou",
    "res.strip": "Obsazenost během večera",
    "res.none": "Na tento den nikdo nemá rezervaci.",
    "res.time": "Čas",
    "res.name": "Jméno",
    "res.party": "Osob",
    "res.phone": "Telefon",
    "res.table": "Stůl",
    "res.note": "Poznámka",
    "res.source": "Odkud",
    "res.status": "Stav",
    "res.source.ONLINE": "web",
    "res.source.PHONE": "telefon",
    "res.source.WALK_IN": "bez rezervace",
    "res.status.BOOKED": "přijde",
    "res.status.SEATED": "usazeni",
    "res.status.NO_SHOW": "nepřišli",
    "res.status.CANCELLED": "zrušeno",
    "res.seat": "Usadit",
    "res.noShow": "Nepřišli",
    "res.cancel": "Zrušit",
    "res.restore": "Vrátit",
    "res.noTable": "— bez stolu —",
    "res.add": "Nová rezervace",
    "res.addHint": "Po telefonu nebo pro hosta, který se zastavil.",
    "res.save": "Zapsat",
    "res.full": "V tu dobu je plno. Zapsat přesto?",
    "res.force": "Zapsat přesto",
    "res.closed": "V tu dobu máte zavřeno.",
    "res.saved": "Zapsáno.",
    "nav.history": "Historie změn",
    "nav.printers": "Tiskárny",
    "print.lead": "Co která tiskárna dostala — i to, co se nevytisklo, a proč.",
    "print.none": "Zatím žádná tiskárna. Kuchyně má obrazovky a účtenky se ukazují na pokladně.",
    "print.empty": "Za posledních 24 hodin nic.",
    "print.unrouted": "Bez tiskárny",
    "print.transport.SCREEN": "Na obrazovce",
    "print.transport.HUB": "Tiskárna v síti podniku",
    "print.transport.CLOUDPRNT": "Tiskárna Star přes CloudPRNT",
    "print.columns": "{n} znaků na řádek",
    "print.receipts": "účtenky z pokladny",
    "print.status.PRINTED": "Vytištěno {at}",
    "print.status.QUEUED": "Čeká na tiskárnu",
    "print.status.STALE": "Nevytištěno: odesláno v {fired}, k tiskárně došlo až v {at}",
    "print.status.UNROUTED": "Nevytištěno: pro {station} netiskne žádná tiskárna",
    "print.copy": "Kopie",
    "print.reprint": "Vytisknout znovu",
    "print.reprinted": "Kopie odeslána na tiskárnu.",
    "print.setup": "Nastavit tiskárnu",
    "print.form.name": "Název",
    "print.form.transport": "Připojení",
    "print.form.columns": "Šířka role",
    "print.form.stations": "Tiskne lístky pro",
    "print.form.receipts": "Tiskne účtenky z pokladny",
    "print.form.active": "Zapnutá",
    "print.form.save": "Uložit tiskárnu",
    "print.saved": "Uloženo — pokladny to vědí.",
    "print.forbidden": "Tiskárny vidí vedoucí směny a výš.",
    "audit.printer.saved": "Tiskárna {subject} nastavena",
    "audit.print.reprinted": "Kopie na tiskárně {subject}",
    "audit.n.lines.one": "{n} položka",
    "audit.n.lines.few": "{n} položky",
    "audit.n.lines.many": "{n} položky",
    "audit.n.lines.other": "{n} položek",
    "audit.n.seats.one": "{n} místo",
    "audit.n.seats.few": "{n} místa",
    "audit.n.seats.many": "{n} místa",
    "audit.n.seats.other": "{n} míst",
    "audit.n.changes.one": "{n} změna",
    "audit.n.changes.few": "{n} změny",
    "audit.n.changes.many": "{n} změny",
    "audit.n.changes.other": "{n} změn",
    "audit.title": "Historie změn",
    "audit.lead": "Kdo co v kanceláři změnil — ceny, lidé, zařízení, sklad. Záznam se nedá upravit, aniž by to bylo vidět.",
    "audit.intact": "Záznam je neporušený — ověřeno: {n}.",
    "audit.broken": "Záznam byl změněn: u změny č. {n} nesouhlasí otisk. Ozvěte se nám.",
    "audit.none": "V tomto období se nic neměnilo.",
    "audit.when": "Kdy",
    "audit.who": "Kdo",
    "audit.what": "Co",
    "audit.system": "Systém",
    "audit.forbidden": "Historii změn vidí vedoucí a majitel.",
    "audit.menu.item.created": "Nové jídlo {subject} za {price}",
    "audit.menu.item.changed": "Úprava jídla {subject}",
    "audit.menu.item.priced": "Cena jídla {subject}: {before} → {after}",
    "audit.menu.published": "Lístek odeslán pokladnám (verze {version})",
    "audit.staff.added": "Nový člen týmu: {subject}",
    "audit.staff.changed": "Změna u {subject}",
    "audit.staff.left": "{subject} už u nás nepracuje",
    "audit.staff.pin.reset": "Nový PIN pro {subject}",
    "audit.device.pairing.issued": "Kód pro spárování nového zařízení",
    "audit.device.unpaired": "Zařízení odpárováno",
    "audit.device.service.changed": "Zařízení přepnuto: {mode}",
    "audit.mode.COUNTER": "pult {prefix}",
    "audit.mode.TABLE": "ke stolům",
    "audit.venue.identity.changed": "Údaje o firmě upraveny",
    "audit.venue.table.saved": "Stůl {subject}: {seats}",
    "audit.venue.published": "Nastavení odesláno pokladnám (verze {version})",
    "audit.venue.modules.changed": "Moduly změněny",
    "audit.venue.public.changed": "Veřejná stránka upravena",
    "audit.stock.received": "Příjem zboží: {lines} za {price}",
    "audit.stock.wasted": "Odpis: {lines}",
    "audit.stock.counted": "Inventura: {lines}",
    "audit.stock.item.saved": "Surovina {subject} upravena",
    "audit.stock.supplier.saved": "Dodavatel {subject}",
    "audit.stock.recipe.versioned": "Receptura {subject}, verze {version}",
    "audit.stock.option-delta.versioned": "Co mění volba hosta, verze {version}",
    "audit.booking.made": "Rezervace pro {party}",
    "audit.booking.forced": "Rezervace pro {party} nad kapacitu",
    "audit.booking.changed": "Rezervace: {status}",
    "nav.stock": "Sklad",
    "stock.tab.overview": "Přehled",
    "stock.tab.items": "Suroviny",
    "stock.tab.recipes": "Receptury",
    "stock.tab.receive": "Příjem",
    "stock.tab.count": "Inventura",
    "stock.tab.waste": "Odpisy",
    "stock.tab.suppliers": "Dodavatelé",
    "stock.value": "Hodnota skladu",
    "stock.valueHint": "V průměrných nákupních cenách bez DPH.",
    "stock.foodCost": "Food cost · podíl surovin",
    "stock.foodCostHint": "Suroviny prodaných jídel jako podíl jejich tržby bez DPH — jen jídla s recepturou.",
    "stock.period.today": "Dnes",
    "stock.period.week": "7 dní",
    "stock.period.month": "30 dní",
    "stock.running": "Dochází",
    "stock.runningNone": "Nic nedochází.",
    "stock.runningLine": "zbývá {left}, objednat pod {min}",
    "stock.byDish": "Podle jídel",
    "stock.dish": "Jídlo",
    "stock.sold": "Prodáno",
    "stock.takings": "Tržba bez DPH",
    "stock.cost": "Suroviny za",
    "stock.share": "Podíl",
    "stock.noSales": "V tomto období se neprodalo nic s recepturou.",
    "stock.unmapped": "Prodeje bez receptury",
    "stock.unmappedHint": "U těchto položek sklad nic neodepisuje. Doplňte jim recepturu — platí od chvíle uložení.",
    "stock.timesSold": "prodáno {n}×",
    "stock.writeRecipe": "Napsat recepturu",
    "stock.problems": "Co sklad nedokázal zapsat",
    "stock.addItem": "Přidat surovinu",
    "stock.item.name": "Název",
    "stock.item.category": "Skupina",
    "stock.item.categoryHint": "Např. Bar, Kuchyně, Sklep — tak se suroviny řadí i v inventuře.",
    "stock.item.onHand": "Na skladě",
    "stock.item.unitCost": "Cena",
    "stock.item.value": "Hodnota",
    "stock.item.unit": "Vede se v",
    "stock.item.unitHint": "Po prvním pohybu na skladě už jednotku nejde změnit.",
    "stock.item.reorder": "Objednat, když klesne pod",
    "stock.item.standardCost": "Cena, než přijde první dodávka",
    "stock.item.standardCostHint": "Kč bez DPH za {unit}. Pak se počítá průměr z dodávek.",
    "stock.item.active": "Používáme",
    "stock.item.low": "dochází",
    "stock.unit.g": "kilogramech (kg)",
    "stock.unit.ml": "litrech (l)",
    "stock.unit.ea": "kusech (ks)",
    "stock.noItems": "Zatím žádné suroviny. Přidejte první — třeba kávu nebo mléko.",
    "stock.perUnit": "za {unit}",
    "stock.saved": "Uloženo.",
    "stock.recipe.lead": "Co spotřebuje jedna porce. Nová verze platí od uložení; co se prodalo dřív, zůstává podle staré.",
    "stock.recipe.portionCost": "Porce stojí",
    "stock.recipe.price": "Cena bez DPH",
    "stock.recipe.none": "bez receptury",
    "stock.recipe.uncosted": "Zatím bez ceny: {items}",
    "stock.recipe.ingredient": "Surovina",
    "stock.recipe.quantity": "Množství",
    "stock.recipe.save": "Uložit novou verzi",
    "stock.recipe.version": "verze {n}",
    "stock.recipe.saved": "Uloženo. Platí pro vše, co se prodá od teď.",
    "stock.options": "Volby hostů",
    "stock.options.lead": "Co volba změní ve skladu, na jeden kus: ovesné mléko je +0,2 l ovesného a −0,2 l mléka.",
    "stock.options.none": "ve skladu nic nemění",
    "stock.options.signedHint": "Kladné množství přidá, záporné ubere.",
    "stock.receive.supplier": "Dodavatel",
    "stock.receive.noSupplier": "— bez dodavatele —",
    "stock.receive.document": "Číslo dodacího listu nebo faktury",
    "stock.receive.netTotal": "Celkem bez DPH (Kč)",
    "stock.receive.submit": "Přijmout na sklad",
    "stock.receive.done": "Přijato. Průměrné ceny jsou přepočítané.",
    "stock.recent": "Poslední pohyby",
    "stock.line.add": "Další řádek",
    "stock.line.remove": "Odebrat řádek",
    "stock.line.pick": "— vyberte surovinu —",
    "stock.line.incomplete": "Doplňte u každého řádku surovinu a množství.",
    "stock.pending": "Zapsáno; sklad se přepočítá během pár vteřin.",
    "stock.count.last": "Poslední inventura · {when}",
    "stock.count.lastExact": "Všechno sedělo.",
    "stock.count.lead": "Spočítejte, co je na regálu, bez ohledu na to, co říká systém. Rozdíly uvidíte dřív, než se cokoli zapíše.",
    "stock.count.counted": "Napočítáno",
    "stock.count.expected": "Podle systému",
    "stock.count.variance": "Rozdíl",
    "stock.count.check": "Zkontrolovat rozdíly",
    "stock.count.post": "Zapsat inventuru",
    "stock.count.back": "Upravit",
    "stock.count.done": "Inventura zapsána. Sklad odpovídá napočítanému.",
    "stock.count.total": "Rozdíl celkem",
    "stock.count.empty": "Napište aspoň jedno množství.",
    "stock.waste.reason": "Důvod",
    "stock.waste.submit": "Odepsat",
    "stock.waste.done": "Odepsáno.",
    "stock.reason.SPOILED": "Zkažené",
    "stock.reason.DAMAGED": "Poškozené",
    "stock.reason.EXPIRED": "Prošlé",
    "stock.reason.PREP_ERROR": "Chyba v kuchyni",
    "stock.reason.RETURNED_BY_GUEST": "Vrácené hostem",
    "stock.reason.STAFF_MEAL": "Jídlo pro personál",
    "stock.reason.VOID_AFTER_FIRE": "Storno po odeslání do kuchyně",
    "stock.reason.COUNT": "Inventura",
    "stock.reason.LATE_BEFORE_COUNT": "Prodej z doby před inventurou",
    "stock.kind.RECEIPT": "Příjem",
    "stock.kind.SALE_DEPLETION": "Prodej",
    "stock.kind.WASTE": "Odpis",
    "stock.kind.STAFF_MEAL": "Personál",
    "stock.kind.COUNT_ADJUSTMENT": "Inventura",
    "stock.supplier.add": "Přidat dodavatele",
    "stock.supplier.registration": "IČO",
    "stock.supplier.none": "Zatím žádní dodavatelé.",
    "today.lowStock": "Dochází: {items}",
    "stock.options.edit": "Upravit",
    "stock.unmappedMore": "a dalších {n}",
    "approvals.title": "Schváleno bez oprávnění",
    "approvals.lead": "Pokladna to měla odmítnout. Prodej platí, jen by to měl někdo prověřit.",
    "approvals.act.VOID_SENT": "Storno jídla, které už měla kuchyně",
    "approvals.act.COMP": "Zdarma",
    "approvals.act.DISCOUNT": "Sleva",
    "approvals.act.REFUND": "Vrácení peněz",
    "approvals.problem.NOT_PERMITTED": "{name} k tomu nemá oprávnění",
    "approvals.problem.UNKNOWN_APPROVER": "schválila neznámá osoba ({name})",
    "approvals.problem.INACTIVE_APPROVER": "{name} už u vás nepracuje",
    "approvals.open": "Otevřít účet",
    "checks.pickup": "Výdej {code}",
    "tender.ONLINE": "Online platba",
    "public.title": "Veřejná stránka a objednávky",
    "public.lead": "Kde vás hosté najdou: jídelní lístek, otevírací doba a objednávky s vyzvednutím.",
    "public.slug": "Adresa stránky",
    "public.slugHint": "Malá písmena, číslice a pomlčky — třeba „lipa-vinohrady“.",
    "public.notYet": "Stránka zatím nemá adresu.",
    "public.open": "Otevřít stránku",
    "public.tagline": "Krátký popis",
    "public.address": "Adresa",
    "public.phone": "Telefon",
    "public.hours": "Otevírací doba",
    "public.hoursHint": "Zavírací čas před otevíracím znamená po půlnoci.",
    "public.closed": "zavřeno",
    "public.addRange": "+ další čas",
    "public.day.mon": "Pondělí",
    "public.day.tue": "Úterý",
    "public.day.wed": "Středa",
    "public.day.thu": "Čtvrtek",
    "public.day.fri": "Pátek",
    "public.day.sat": "Sobota",
    "public.day.sun": "Neděle",
    "public.online": "Přijímáme online objednávky",
    "public.onlineHint": "Vypnutím objednávky hned pozastavíte — třeba když je plno.",
    "public.prep": "Příprava (minuty)",
    "public.slot": "Časy vyzvednutí po (minutách)",
    "public.bookings": "Přijímáme rezervace online",
    "public.bookingsHint": "Hosté si stůl zarezervují na vaší stránce. Po telefonu můžete rezervovat vždy.",
    "public.capacity": "Hostů najednou",
    "public.duration": "Stůl držíme (minuty)",
    "public.bookingSlot": "Časy rezervací po (minutách)",
    "public.maxParty": "Největší skupina online",
    "public.daysAhead": "Kolik dní dopředu",
    "public.notice": "Nejpozději předem (minuty)",
    "public.saved": "Uloženo. Stránka se změní hned.",
    "menu.guests": "Pro hosty online",
    "menu.photo": "Fotka",
    "menu.photoUpload": "Nahrát fotku",
    "menu.photoRemove": "Odebrat fotku",
    "menu.photoHint": "Zmenšíme ji v prohlížeči — na server jde jen malá kopie.",
    "menu.photoFailed": "Fotku se nepodařilo nahrát.",
    "menu.nameEn": "Název anglicky",
    "menu.descriptionEn": "Popis anglicky",
    "menu.dietary": "Vhodné pro",
    "menu.diet.VEGAN": "vegany",
    "menu.diet.VEGETARIAN": "vegetariány",
    "menu.diet.GLUTEN_FREE": "bez lepku",
    "menu.diet.LACTOSE_FREE": "bez laktózy",
    "menu.diet.SPICY": "pálivé",
    "menu.featured": "Doporučujeme — nahoře na stránce",
  },
};
